import React, { useRef, useEffect, useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import DraggableHandCard from './DraggableHandCard';

const DroppableHandBar = ({ 
  hand, 
  setHand, 
  setSelectedCard,
  setShowCardInfoModal,
  isDragging
}) => {
  // ============================================================================
  // HAND DROP ZONE IMPLEMENTATION
  // ============================================================================
  // 
  // This component creates a droppable hand zone that:
  // 1. Accepts cards from field, graveyard, banished, deck, and extra deck
  // 2. Provides visual feedback when cards are dragged over it
  // 3. Uses unique ID 'drop-hand' to avoid conflicts with zone IDs
  // 4. Integrates with the centralized drag and drop logic in DuelDragPage
  // 5. Auto-scrolls to newly added cards and highlights them
  //
  // Key features:
  // - useDroppable: Creates drop target with ID "drop-hand"
  // - Visual feedback: Highlights when cards are dragged over
  // - Auto-scroll: Scrolls to newly added cards
  // - Card highlighting: Highlights new cards briefly
  // - No complex logic: All card movement handled centrally
  // ============================================================================

  const handContainerRef = useRef(null);
  const [highlightedCardId, setHighlightedCardId] = useState(null);
  const [previousHandLength, setPreviousHandLength] = useState(hand.length);

  // Auto-scroll to newly added cards
  useEffect(() => {
    if (hand.length > previousHandLength) {
      // A card was added to the hand
      const newCard = hand[hand.length - 1];
      if (newCard && handContainerRef.current) {
        // Highlight the new card
        setHighlightedCardId(newCard.id);
        
        // Auto-scroll to the new card with better targeting
        const scrollToNewCard = () => {
                  // Target the correct scroll container (.hand-bar-drag)
        const scrollContainer = handContainerRef.current?.closest('.hand-bar-drag');
        
        if (scrollContainer && scrollContainer.scrollWidth > scrollContainer.clientWidth) {
          const scrollWidth = scrollContainer.scrollWidth;
          const clientWidth = scrollContainer.clientWidth;
          const maxScroll = scrollWidth - clientWidth;
          
          console.log('🔄 Auto-scrolling to new card:', {
            container: scrollContainer.className,
            scrollWidth,
            clientWidth,
            maxScroll,
            currentScroll: scrollContainer.scrollLeft
          });
          
          // Scroll to the rightmost position to show the new card
          scrollContainer.scrollTo({
            left: maxScroll,
            behavior: 'smooth'
          });
        } else {
          console.log('⚠️ No scrollable container found or no scroll needed');
        }
        };
        
        // Try scrolling with different timing strategies
        setTimeout(scrollToNewCard, 100); // Immediate attempt
        setTimeout(scrollToNewCard, 300); // After card renders
        setTimeout(scrollToNewCard, 600); // Backup attempt
        
        // Use requestAnimationFrame for better timing
        requestAnimationFrame(() => {
          setTimeout(scrollToNewCard, 50);
        });
        
        // Remove highlight after 2 seconds
        setTimeout(() => {
          setHighlightedCardId(null);
        }, 2000);
      }
    }
    setPreviousHandLength(hand.length);
  }, [hand, previousHandLength]);

  const { setNodeRef, isOver, active } = useDroppable({
    id: 'drop-hand', // Unique ID for hand drop zone
    data: {
      zone: 'hand',
      accepts: ['card'],
    },
  });

  // Determine if we should show drop highlighting
  // Highlight when any card is being dragged over the hand zone
  // This provides clear visual feedback for the drop target
  const shouldHighlight = isOver && active;

  // Additional check to prevent highlighting when dragging hand cards to hand
  // This prevents confusion about hand-to-hand moves
  const isHandToHandMove = active?.data.current?.from === 'hand';
  const finalHighlight = shouldHighlight && !isHandToHandMove;

  // Debug logging to understand drop detection
  if (isOver || active) {
    console.log('🔍 Hand drop zone state:', {
      isOver,
      activeId: active?.id,
      activeData: active?.data.current,
      shouldHighlight,
      isHandToHandMove,
      finalHighlight
    });
  }

  // Log when the component renders to ensure it's mounted
  console.log('🔄 DroppableHandBar render:', {
    handLength: hand.length,
    isOver,
    active: !!active,
    finalHighlight
  });

  // Log the useDroppable hook state
  console.log('🎯 useDroppable hook state:', {
    id: 'drop-hand',
    isOver,
    active: !!active,
    activeId: active?.id
  });

  return (
    <div 
      ref={setNodeRef}
      className="hand-bar-drag" // NEW CLASS NAME to avoid CSS conflicts
      style={{
        // Transparent background with minimal visual feedback
        background: finalHighlight 
          ? 'rgba(255, 107, 53, 0.1)' 
          : 'transparent',
        border: finalHighlight 
          ? '2px dashed #ff6b35' 
          : 'none',
        transition: 'all 0.3s ease',
        // Improved dimensions and positioning
        position: 'relative !important',
        bottom: 'auto !important',
        left: 'auto !important',
        width: '100% !important',
        height: '180px !important', // Increased height for better visual balance
        minHeight: '180px !important', // Increased height for better visual balance
        zIndex: 1000,
        // Enhanced padding and margins
        padding: '15px 20px !important',
        margin: '10px 5px !important',
        borderRadius: '12px !important', // More rounded corners
        // Minimal drop zone visibility
        boxShadow: finalHighlight 
          ? '0 0 15px rgba(255, 107, 53, 0.3)' 
          : 'none',
        // CRITICAL: Completely override ALL transforms with multiple approaches
        transform: 'none !important',
        WebkitTransform: 'none !important',
        MozTransform: 'none !important',
        msTransform: 'none !important',
        // Ensure proper display and overflow for horizontal scrolling
        display: 'block !important',
        overflow: 'hidden !important',
        // Remove any CSS transforms that might interfere
        whiteSpace: 'nowrap !important',
        scrollBehavior: 'smooth !important',
        // Additional properties to ensure no inheritance from problematic CSS
        maxHeight: 'none !important',
        overflowX: 'auto !important',
        overflowY: 'hidden !important',
        scrollSnapType: 'none !important',
        scrollSnapAlign: 'none !important',
        scrollSnapStop: 'normal !important',
        // Ensure proper positioning
        top: 'auto !important',
        right: 'auto !important',
        float: 'none !important',
        clear: 'none !important',
      }}
      // Add a title for accessibility and debugging
      title={`Hand Zone - ${hand.length} cards${finalHighlight ? ' (Drop target active)' : ''}`}
      // Add click handler for debugging
      onClick={() => {
        console.log('🖱️ Hand zone clicked! This confirms the component is working.');
        console.log('🔍 Current drop zone state:', { isOver, active: !!active, finalHighlight });
      }}
    >


      <div className="hand-inner-drag" ref={handContainerRef}>
        {/* Render hand cards with enhanced styling and horizontal row layout */}
        {hand.length > 0 ? (
          hand.map((card, index) => (
            <DraggableHandCard
              key={`hand-card-${card.id}-${index}`} // Use card ID + index for truly unique React keys
              // This prevents duplicate key warnings when hand contains duplicate cards
              card={card}
              index={index}
              totalCards={hand.length} // Pass total cards for dynamic squeezing
              setSelectedCard={setSelectedCard}
              setShowCardInfoModal={setShowCardInfoModal}
              globalIsDragging={isDragging} // Pass the global dragging state
              isHighlighted={highlightedCardId === card.id} // Pass the highlighted state
            />
          ))
        ) : (
          <div className="hand-placeholder" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            color: finalHighlight ? '#ff6b35' : 'rgba(255, 255, 255, 0.6)',
            fontSize: '16px',
            fontWeight: '500',
            textAlign: 'center',
            minHeight: '80px',
          }}>
            <div style={{
              fontSize: '24px',
              marginBottom: '8px',
              opacity: 0.7,
            }}>
              🎴
            </div>
            <span>
              {finalHighlight ? 'Drop cards here!' : 'Drop cards here'}
            </span>
            <div style={{
              fontSize: '12px',
              marginTop: '4px',
              opacity: 0.5,
            }}>
              Drag cards from field, deck, or other zones
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DroppableHandBar; 